import { Box, Icon, Paper, type PaperProps, Typography } from "@mui/material";
import type React from "react";

const contentPaperKeys = {
	header: "content-paper-header",
	body: "content-paper-body",
	footer: "content-paper-footer",
} as const;

const contentPaperHeaderKeys = {
	headerSection: "content-paper-header-title",
	actionsSection: "content-paper-header-actions",
	secondaryRowSection: "content-paper-header-secondary-row",
} as const;

interface ContentPaperHeaderProps {
	title?: string;
	icon?: React.ReactNode;
	customHeader?: React.ReactNode;
	children?: React.ReactNode;
	secondaryRow?: React.ReactNode;
	paperProps?: Omit<PaperProps, "children">;
}

const ContentPaperHeader: React.FC<ContentPaperHeaderProps> = ({
	customHeader,
	children,
	secondaryRow,
	icon,
	title,
	paperProps,
}) => {
	const hasChildren = Boolean(children);
	const hasSecondaryRow = Boolean(secondaryRow);

	const gridTemplateAreas = [
		`"${contentPaperKeys.header} ${hasChildren ? contentPaperHeaderKeys.actionsSection : contentPaperKeys.header}"`,
		hasSecondaryRow
			? `"${contentPaperHeaderKeys.secondaryRowSection} ${contentPaperHeaderKeys.secondaryRowSection}"`
			: `"${contentPaperKeys.header} ${hasChildren ? contentPaperHeaderKeys.actionsSection : contentPaperKeys.header}"`,
	]
		.filter(Boolean)
		.join("\n");

	return (
		<Paper
			elevation={0}
			square={true}
			style={{
				gridArea: contentPaperKeys.header,
				display: "grid",
				gridAutoColumns: "1fr",
				gridTemplateColumns: hasChildren ? "1fr auto" : "1fr",
				gridTemplateRows: hasSecondaryRow ? "1fr 1fr" : "1fr",
				gridTemplateAreas: gridTemplateAreas,
				boxSizing: "border-box",
				padding: "0.5em",
			}}
			sx={[
				(theme) => ({
					backgroundColor: theme?.vars?.palette.grey[200],
				}),
				(theme) =>
					theme.applyStyles("dark", {
						backgroundColor: theme?.vars?.palette.grey[900],
					}),
			]}
			{...paperProps}
		>
			{customHeader ? (
				customHeader
			) : (
				<Box
					display="flex"
					flexDirection="row"
					justifyContent="flex-start"
					alignItems="center"
					gridArea={contentPaperKeys.header}
					gap="0.5em"
				>
					<Icon
						fontSize="large"
						color="primary"
						sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
					>
						{icon}
					</Icon>
					<Typography color="secondary" margin={0} fontSize="1.3rem" fontWeight="normal" component="h3">
						{title}
					</Typography>
				</Box>
			)}
			{hasChildren && <Box gridArea={contentPaperHeaderKeys.actionsSection}>{children}</Box>}
			{hasSecondaryRow && <Box gridArea={contentPaperHeaderKeys.secondaryRowSection}>{secondaryRow}</Box>}
		</Paper>
	);
};

const ContentPaperBody: React.FC<PaperProps> = ({ children, ...props }) => {
	return (
		<Paper
			elevation={0}
			square={true}
			style={{
				gridArea: contentPaperKeys.body,
				overflow: "auto",
			}}
			{...props}
		>
			{children}
		</Paper>
	);
};

const ContentPaperFooter: React.FC<PaperProps> = ({ children, ...props }) => {
	return (
		<Paper
			elevation={0}
			square={true}
			style={{
				gridArea: contentPaperKeys.footer,
			}}
			sx={[
				(theme) => ({
					backgroundColor: theme?.vars?.palette.grey[200],
					boxSizing: "border-box",
					padding: "0.5em",
				}),
				(theme) =>
					theme.applyStyles("dark", {
						backgroundColor: theme?.vars?.palette.grey[900],
						boxSizing: "border-box",
						padding: "0.5em",
					}),
			]}
			{...props}
		>
			{children}
		</Paper>
	);
};

const ContentPaperBase: React.FC<PaperProps> = ({ children, ...props }) => {
	return (
		<Paper
			sx={{
				height: "100%",
				width: "100%",
				display: "grid",
				gridAutoColumns: "1fr",
				gridTemplateColumns: "1fr",
				gridTemplateRows: "auto 1fr auto",
				gridTemplateAreas: `
      "${contentPaperKeys.header}"
      "${contentPaperKeys.body}"
      "${contentPaperKeys.footer}"
      `,
			}}
			{...props}
		>
			{children}
		</Paper>
	);
};

interface ContentPaperComponentsProps {
	Header: typeof ContentPaperHeader;
	Body: typeof ContentPaperBody;
	Footer: typeof ContentPaperFooter;
}

type ContentPaperType = typeof ContentPaperBase & ContentPaperComponentsProps;

const ContentPaper: ContentPaperType = ContentPaperBase as ContentPaperType;
ContentPaper.Header = ContentPaperHeader;
ContentPaper.Body = ContentPaperBody;
ContentPaper.Footer = ContentPaperFooter;

export default ContentPaper;
